/**
 * TanStack DB Collections — FlowState Automate local-first data layer.
 *
 * Each collection is backed by an Electric durable shape stream and provides:
 *  - Instant UI reads via a reactive in-memory cache (`useLiveQuery`)
 *  - Background sync via Electric → Supabase Postgres write-through
 *  - Optimistic mutations with automatic rollback on failure (Phase 3)
 *
 * Collections are created by factory functions (not singletons) because they
 * must be scoped to the authenticated user and organisation. Cache the
 * returned collection at the route/context level; do not recreate on every
 * render.
 *
 * Usage:
 * ```tsx
 * import { createTodosCollection } from '@/lib/db'
 * import { useLiveQuery } from '@tanstack/react-db'
 *
 * // Stable reference — create once per userId/orgId pair:
 * const todos = useMemo(
 *   () => createTodosCollection(userId, orgId),
 *   [userId, orgId]
 * )
 *
 * // In a component:
 * const { data } = useLiveQuery((q) => q.from({ todos }).select())
 * ```
 */

import { createCollection } from "@tanstack/db";
import { getShapeUrlForTable, buildWhereClause, isValidUuid } from "../electric";
import { createElectricSyncConfig } from "./electric-sync-provider";
import type { ShapeScope } from "./electric-sync-provider";
import {
  persistDeletes,
  persistInserts,
  persistUpdates,
} from "./mutation-handlers";
import type { CortexEventLogRow, CortexRunRow } from "@flowstate/types";
import type {
  NutritionEntry,
  ExerciseEntry,
  HealthMetricEntry,
} from "@/features/health/types";

// ── UUID helpers (re-exported for consumers) ─────────────────────────────────

export { isValidUuid };

/**
 * Build a safe UUID equality WHERE clause fragment.
 * Returns `undefined` when the value is empty or not a valid UUID.
 */
export function buildSafeEq(column: string, value: string): string | undefined {
  return buildWhereClause(column, value);
}

// ── Shape URL builder ────────────────────────────────────────────────────────

function shapeUrl(table: string): string {
  return getShapeUrlForTable(table);
}

// ── Row types ────────────────────────────────────────────────────────────────

export interface TodoRow {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  due_date?: string | null;
  created_by: string;
  assigned_to?: string | null;
  assigned_by?: string | null;
  assigned_at?: string | null;
  organization_id?: string | null;
  created_at: string;
  updated_at: string;
  parent_id?: string | null;
  category_id?: string | null;
  client_id?: string | null;
  project_id?: string | null;
  is_recurring?: boolean | null;
  recurrence_pattern_id?: string | null;
  recurring_parent_id?: string | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  actual_start?: string | null;
  actual_end?: string | null;
  completed_at?: string | null;
  template_id?: string | null;
  time_block_id?: string | null;
  time_tracked?: number | null;
  google_sheet_row_id?: number | null;
  [key: string]: unknown;
}

export interface ProfileRow {
  id: string;
  full_name?: string | null;
  avatar_url?: string | null;
  email?: string | null;
  rss_feeds?: string[] | null;
  [key: string]: unknown;
  created_at?: string;
  updated_at?: string;
}

export interface AccountRow {
  id: string;
  user_id: string;
  organization_id?: string | null;
  name: string;
  type?: string | null;
  kind: string;
  is_default: boolean;
  created_at?: string;
}

export interface CategoryAccountingRow {
  id: string;
  user_id: string;
  organization_id?: string | null;
  name: string;
  kind: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface NotificationRow {
  id: string;
  user_id: string;
  actor_id: string | null;
  type: string;
  entity_type: string | null;
  entity_id: string | null;
  message_preview: string | null;
  data: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  archived_at?: string | null;
  needs_multi_channel?: boolean | null;
  created_at: string;
}

export interface TimeBlockRow {
  id: string;
  title: string;
  description?: string | null;
  start_time: string;
  end_time: string;
  block_type: "focus" | "meeting" | "break" | "admin" | "personal" | string;
  color?: string | null;
  task_ids?: string[] | null;
  is_locked?: boolean | null;
  recurrence_pattern_id?: string | null;
  calendar_event_id?: string | null;
  user_id: string;
  organization_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientRow {
  id: string;
  name: string;
  email: string | null;
  phone?: string | null;
  company?: string | null;
  notes?: string | null;
  status?: string | null;
  source?: string | null;
  priority?: string | null;
  industry?: string | null;
  lifecycle_stage?: string | null;
  lead_status?: string | null;
  contact_person?: string | null;
  contact_title?: string | null;
  secondary_email?: string | null;
  secondary_phone?: string | null;
  website?: string | null;
  address?: Record<string, unknown> | null;
  social_profiles?: Record<string, unknown> | null;
  employee_count?: number | null;
  annual_revenue?: number | null;
  estimated_value?: number | null;
  engagement_score?: number | null;
  conversion_probability?: number | null;
  account_manager_commission_rate?: number | null;
  business_type?: string | null;
  tier?: string | null;
  territory_id?: string | null;
  partner_id?: string | null;
  tags?: string[] | null;
  metadata?: Record<string, unknown> | null;
  user_id?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  assigned_to?: string | null;
  organization_id?: string | null;
  last_contact_date?: string | null;
  next_follow_up_date?: string | null;
  drive_folder_id?: string | null;
  google_resource_name?: string | null;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface OpportunityRow {
  id: string;
  client_id: string;
  organization_id?: string | null;
  title: string;
  value: number;
  stage: string;
  probability?: number | null;
  sales_rep_id?: string | null;
  expected_close_date?: string | null;
  actual_close_date?: string | null;
  source?: string | null;
  tags?: string[] | null;
  notes?: string | null;
  description?: string | null;
  currency?: string | null;
  project_id?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface ClientActivityRow {
  id: string;
  client_id: string;
  type: string;
  title: string;
  description?: string | null;
  outcome?: string | null;
  assigned_to?: string | null;
  created_by: string;
  related_deal_id?: string | null;
  related_task_id?: string | null;
  scheduled_at?: string | null;
  completed_at?: string | null;
  duration_minutes?: number | null;
  attachments?: string[] | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface TaskCommentRow {
  id: string;
  task_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface TaskDependencyRow {
  id: string;
  predecessor_task_id: string;
  successor_task_id: string;
  dependency_type: string;
  lag_time?: number | null;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

// Health table row types use the domain types directly (same shape).
export type NutritionEntryRow = NutritionEntry;
export type ExerciseEntryRow = ExerciseEntry;
export type HealthMetricEntryRow = HealthMetricEntry;
/**
 * Flat DB shape of `health_goals` (the app-level `HealthGoals` type nests
 * the nutrition columns — `useHealthShapes` maps rows → `HealthGoals`).
 */
export interface HealthGoalsRow {
  id: string;
  user_id: string;
  organization_id?: string | null;
  client_id?: string | null;
  daily_calories: number;
  daily_protein_g: number;
  daily_carbs_g: number;
  daily_fat_g: number;
  daily_water_ml: number;
  daily_fiber_g: number;
  target_weight_kg?: number | null;
  weekly_exercise_minutes: number;
  weekly_workout_count: number;
  daily_steps: number;
  daily_sleep_hours: number;
  set_by?: string | null;
  updated_at: string;
  [key: string]: unknown;
}

// ── Collection factories ─────────────────────────────────────────────────────

/**
 * Create a todos collection scoped to a user and optional org.
 */
export function createTodosCollection(
  userId?: string,
  orgId?: string,
  signal?: AbortSignal
) {
  // Org-shared table: when an org is active the shape covers every org task
  // (created_by AND assigned_to visibility are client-side filters on top).
  // Personal scope falls back to the caller's own rows. The proxy verifies
  // org membership / self-scope and builds the WHERE server-side.
  const scope: ShapeScope = { user: userId, org: orgId };

  return createCollection<TodoRow>({
    id: `todos:${userId ?? "all"}:${orgId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<TodoRow>({
      url: shapeUrl("todos"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("todos", transaction, "todos"),
    onUpdate: ({ transaction }) =>
      persistUpdates("todos", transaction, "todos"),
    onDelete: ({ transaction }) =>
      persistDeletes("todos", transaction, "todos"),
  });
}

/**
 * Create a profiles collection scoped to a specific user.
 */
export function createProfilesCollection(
  userId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId };

  return createCollection<ProfileRow>({
    id: `profiles:${userId ?? "all"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<ProfileRow>({
      url: shapeUrl("profiles"),
      scope,
      signal,
    }),
    onUpdate: ({ transaction }) =>
      persistUpdates("profiles", transaction, "profiles"),
  });
}

/**
 * Create an accounts collection scoped to an org or user.
 */
export function createAccountsCollection(
  userId?: string,
  orgId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId, org: orgId };

  return createCollection<AccountRow>({
    id: `accounts:${userId ?? "all"}:${orgId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<AccountRow>({
      url: shapeUrl("accounts"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("accounts", transaction, "accounts"),
    onUpdate: ({ transaction }) =>
      persistUpdates("accounts", transaction, "accounts"),
    onDelete: ({ transaction }) =>
      persistDeletes("accounts", transaction, "accounts"),
  });
}

/**
 * Create a categories_accounting collection scoped to a user.
 */
export function createCategoriesAccountingCollection(
  userId?: string,
  orgId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId, org: orgId };

  return createCollection<CategoryAccountingRow>({
    id: `categories_accounting:${userId ?? "all"}:${orgId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<CategoryAccountingRow>({
      url: shapeUrl("categories_accounting"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("categories_accounting", transaction, "categories_accounting"),
    onUpdate: ({ transaction }) =>
      persistUpdates("categories_accounting", transaction, "categories_accounting"),
    onDelete: ({ transaction }) =>
      persistDeletes("categories_accounting", transaction, "categories_accounting"),
  });
}

/**
 * Create a notifications collection scoped to a user.
 */
export function createNotificationsCollection(
  userId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId };

  return createCollection<NotificationRow>({
    id: `notifications:${userId ?? "all"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<NotificationRow>({
      url: shapeUrl("notifications"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("notifications", transaction, "notifications"),
    onUpdate: ({ transaction }) =>
      persistUpdates("notifications", transaction, "notifications"),
    onDelete: ({ transaction }) =>
      persistDeletes("notifications", transaction, "notifications"),
  });
}

/**
 * Create a time_blocks collection scoped to a user and optional org.
 */
export function createTimeBlocksCollection(
  userId?: string,
  orgId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId, org: orgId };

  return createCollection<TimeBlockRow>({
    id: `time_blocks:${userId ?? "all"}:${orgId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<TimeBlockRow>({
      url: shapeUrl("time_blocks"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("time_blocks", transaction, "time_blocks"),
    onUpdate: ({ transaction }) =>
      persistUpdates("time_blocks", transaction, "time_blocks"),
    onDelete: ({ transaction }) =>
      persistDeletes("time_blocks", transaction, "time_blocks"),
  });
}

/**
 * Create a clients collection scoped to an org.
 */
export function createClientsCollection(
  userId?: string,
  orgId?: string,
  signal?: AbortSignal
) {
  // Org-shared table: org scope when an org is active, personal scope by
  // created_by otherwise (personal workspace passes userId). The proxy
  // verifies org membership / self-scope and builds the WHERE server-side.
  const scope: ShapeScope = { user: userId, org: orgId };

  return createCollection<ClientRow>({
    id: `clients:${userId ?? "all"}:${orgId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<ClientRow>({
      url: shapeUrl("clients"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("clients", transaction, "clients"),
    onUpdate: ({ transaction }) =>
      persistUpdates("clients", transaction, "clients"),
    onDelete: ({ transaction }) =>
      persistDeletes("clients", transaction, "clients"),
  });
}

/**
 * Create an opportunities collection scoped to an organization.
 *
 * opportunities has no user-level RLS scoping column for a personal shape;
 * org scoping is the canonical filter. When orgId is absent the shape syncs
 * everything RLS exposes — pass orgId for production reads.
 */
export function createOpportunitiesCollection(
  orgId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { org: orgId };

  return createCollection<OpportunityRow>({
    id: `opportunities:${orgId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<OpportunityRow>({
      url: shapeUrl("opportunities"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("opportunities", transaction, "opportunities"),
    onUpdate: ({ transaction }) =>
      persistUpdates("opportunities", transaction, "opportunities"),
    onDelete: ({ transaction }) =>
      persistDeletes("opportunities", transaction, "opportunities"),
  });
}

/**
 * Create a client_activities collection scoped to a client (or the creating
 * user when no clientId is given). client_activities has no organization_id
 * column, so shapes scope by client_id or created_by.
 */
export function createClientActivitiesCollection(
  clientId?: string,
  userId?: string,
  signal?: AbortSignal
) {
  // client_activities has no organization_id column — the proxy validates
  // scope_client access via the clients table, or falls back to self-scope
  // on created_by.
  const scope: ShapeScope = { client: clientId, user: userId };

  return createCollection<ClientActivityRow>({
    id: `client_activities:${clientId ?? "user"}:${userId ?? "all"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<ClientActivityRow>({
      url: shapeUrl("client_activities"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("client_activities", transaction, "client_activities"),
    onUpdate: ({ transaction }) =>
      persistUpdates("client_activities", transaction, "client_activities"),
    onDelete: ({ transaction }) =>
      persistDeletes("client_activities", transaction, "client_activities"),
  });
}

/**
 * Create a task_comments collection scoped to a single task.
 * Mount one per open task detail view — comments need per-task shapes.
 */
export function createTaskCommentsCollection(
  taskId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { task: taskId };

  return createCollection<TaskCommentRow>({
    id: `task_comments:${taskId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<TaskCommentRow>({
      url: shapeUrl("task_comments"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("task_comments", transaction, "task_comments"),
    onUpdate: ({ transaction }) =>
      persistUpdates("task_comments", transaction, "task_comments"),
    onDelete: ({ transaction }) =>
      persistDeletes("task_comments", transaction, "task_comments"),
  });
}

/**
 * Create a task_dependencies collection scoped to a single task —
 * edges where the task is predecessor OR successor.
 */
export function createTaskDependenciesCollection(
  taskId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { task: taskId };

  return createCollection<TaskDependencyRow>({
    id: `task_dependencies:${taskId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<TaskDependencyRow>({
      url: shapeUrl("task_dependencies"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("task_dependencies", transaction, "task_dependencies"),
    onUpdate: ({ transaction }) =>
      persistUpdates("task_dependencies", transaction, "task_dependencies"),
    onDelete: ({ transaction }) =>
      persistDeletes("task_dependencies", transaction, "task_dependencies"),
  });
}

/**
 * Create a nutrition_entries collection scoped to a user.
 */
export function createNutritionEntriesCollection(
  userId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId };

  return createCollection<NutritionEntryRow>({
    id: `nutrition_entries:${userId ?? "all"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<NutritionEntryRow>({
      url: shapeUrl("nutrition_entries"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("nutrition_entries", transaction, "nutrition_entries"),
    onUpdate: ({ transaction }) =>
      persistUpdates("nutrition_entries", transaction, "nutrition_entries"),
    onDelete: ({ transaction }) =>
      persistDeletes("nutrition_entries", transaction, "nutrition_entries"),
  });
}

/**
 * Create an exercise_entries collection scoped to a user.
 */
export function createExerciseEntriesCollection(
  userId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId };

  return createCollection<ExerciseEntryRow>({
    id: `exercise_entries:${userId ?? "all"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<ExerciseEntryRow>({
      url: shapeUrl("exercise_entries"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("exercise_entries", transaction, "exercise_entries"),
    onUpdate: ({ transaction }) =>
      persistUpdates("exercise_entries", transaction, "exercise_entries"),
    onDelete: ({ transaction }) =>
      persistDeletes("exercise_entries", transaction, "exercise_entries"),
  });
}

/**
 * Create a health_metric_entries collection scoped to a user.
 */
export function createHealthMetricEntriesCollection(
  userId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId };

  return createCollection<HealthMetricEntryRow>({
    id: `health_metric_entries:${userId ?? "all"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<HealthMetricEntryRow>({
      url: shapeUrl("health_metric_entries"),
      scope,
      signal,
    }),
    onInsert: ({ transaction }) =>
      persistInserts("health_metric_entries", transaction, "health_metric_entries"),
    onUpdate: ({ transaction }) =>
      persistUpdates("health_metric_entries", transaction, "health_metric_entries"),
    onDelete: ({ transaction }) =>
      persistDeletes("health_metric_entries", transaction, "health_metric_entries"),
  });
}

/**
 * Create a health_goals collection scoped to a user.
 */
export function createHealthGoalsCollection(
  userId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { user: userId };

  return createCollection<HealthGoalsRow>({
    id: `health_goals:${userId ?? "all"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<HealthGoalsRow>({
      url: shapeUrl("health_goals"),
      scope,
      signal,
    }),
    onUpdate: ({ transaction }) =>
      persistUpdates("health_goals", transaction, "health_goals"),
  });
}

/**
 * Create a cortex_events_log collection scoped to an organization.
 */
export function createCortexEventsLogCollection(
  orgId?: string,
  signal?: AbortSignal
) {
  const scope: ShapeScope = { org: orgId };

  return createCollection<CortexEventLogRow>({
    id: `cortex_events_log:${orgId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<CortexEventLogRow>({
      url: shapeUrl("cortex_events_log"),
      scope,
      signal,
    }),
  });
}

/**
 * Create a cortex_runs collection scoped to a chat session (Synaptic Awakening).
 */
export function createCortexRunsCollection(
  sessionId?: string,
  signal?: AbortSignal
) {
  // scope_session is validated against the caller's chat_sessions access.
  const scope: ShapeScope = { session: sessionId };

  return createCollection<CortexRunRow>({
    id: `cortex_runs:${sessionId ?? "global"}`,
    getKey: (row) => row.id,
    sync: createElectricSyncConfig<CortexRunRow>({
      url: shapeUrl("cortex_runs"),
      scope,
      signal,
    }),
  });
}
